/**
 * Iter XX.139 — Founder Transparency: Decision Trace.
 *
 * Founder-only debug surface. For any conversation, this page shows:
 *   · Every signal Gully computed (trust, insight, curiosity,
 *     commitment, confidence, emotional_state, long_term_intent,
 *     continued_coaching_benefit)
 *   · The decision the Interaction Decision Engine picked
 *   · Every gate that was evaluated and why it fired / was skipped
 *   · The exact personalized Plus explanation (if one exists)
 *   · Why that explanation was selected
 *   · Why Plus was or was not introduced
 *
 * The coaching brain must never become a black box.
 *
 * Server gates data behind `_require_admin`, so this component just
 * needs to authenticate the fetch (via axios interceptor) and render.
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";

function num(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return v.toFixed(2);
  return String(v);
}

function BarRow({ label, value, testId }) {
  const v = typeof value === "number" ? Math.max(0, Math.min(1, value)) : 0;
  const pct = Math.round(v * 100);
  const tone = pct >= 60 ? "bg-emerald-500" : pct >= 30 ? "bg-amber-500" : "bg-neutral-600";
  return (
    <div className="flex items-center gap-3 text-xs" data-testid={testId}>
      <div className="w-40 text-neutral-300">{label}</div>
      <div className="flex-1 h-1.5 bg-neutral-800 rounded overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="w-12 text-right text-neutral-400 tabular-nums">
        {num(value)}
      </div>
    </div>
  );
}

function GateRow({ gate }) {
  const outcome = gate.outcome || "unknown";
  const tone =
    outcome === "fired"
      ? "bg-emerald-900/50 text-emerald-200 border-emerald-800"
      : outcome === "skipped"
      ? "bg-neutral-900/60 text-neutral-400 border-neutral-800"
      : "bg-amber-900/40 text-amber-200 border-amber-800";
  return (
    <div
      data-testid={`gate-row-${gate.gate}`}
      className={`border rounded-md p-3 ${tone}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="font-medium text-xs">{gate.gate}</div>
        <div
          data-testid={`gate-outcome-${gate.gate}`}
          className="text-[10px] uppercase tracking-wider"
        >
          {outcome}
        </div>
      </div>
      <div className="text-xs opacity-90">{gate.note}</div>
      {gate.conditions ? (
        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-mono">
          {Object.entries(gate.conditions).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-2">
              <span className="text-neutral-500">{k}</span>
              <span className={v ? "text-emerald-300" : "text-neutral-500"}>
                {String(v)}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DecisionCard({ d, idx }) {
  const [rawOpen, setRawOpen] = useState(false);
  const isPlus = d.action === "introduce_plus";
  return (
    <div
      data-testid={`decision-card-${idx}`}
      className="border border-neutral-800 rounded-lg p-4 bg-neutral-950"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
        <div>
          <div
            data-testid={`decision-action-${idx}`}
            className={`text-sm font-semibold ${
              isPlus ? "text-emerald-300" : "text-neutral-100"
            }`}
          >
            {d.action}
          </div>
          <div className="text-[11px] text-neutral-500 font-mono">
            {d.reason}
            {d.high_intent_bypass ? " · high_intent_bypass" : ""}
          </div>
        </div>
        <div className="text-[10px] text-neutral-500 font-mono">
          {d.at} · turn {d.turn_number ?? "?"} · {d.surface}
        </div>
      </div>

      {d.user_message_preview ? (
        <div
          data-testid={`decision-message-${idx}`}
          className="text-xs italic text-neutral-400 mb-4 border-l-2 border-neutral-800 pl-3"
        >
          &ldquo;{d.user_message_preview}&rdquo;
        </div>
      ) : null}

      {/* SIGNALS */}
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">
          Signals (this turn)
        </div>
        <div className="space-y-1.5">
          <BarRow label="Trust" value={d.signals?.trust} testId={`sig-trust-${idx}`} />
          <BarRow label="Insight (aha)" value={d.signals?.insight} testId={`sig-insight-${idx}`} />
          <BarRow label="Value" value={d.signals?.value} testId={`sig-value-${idx}`} />
          <BarRow label="Curiosity" value={d.signals?.curiosity} testId={`sig-curiosity-${idx}`} />
          <BarRow label="Commitment" value={d.signals?.commitment} testId={`sig-commitment-${idx}`} />
          <BarRow label="Confidence" value={d.signals?.confidence} testId={`sig-confidence-${idx}`} />
          <BarRow label="Long-term intent" value={d.signals?.long_term_intent} testId={`sig-longterm-${idx}`} />
          <BarRow label="Frustration (soft)" value={d.signals?.frustration} testId={`sig-frustration-${idx}`} />
        </div>
        <div className="mt-2 text-xs text-neutral-400">
          Emotional state:{" "}
          <span
            data-testid={`emotional-state-${idx}`}
            className="font-mono text-neutral-200"
          >
            {d.signals?.emotional_state || "—"}
          </span>
        </div>
      </div>

      {/* META SCORES */}
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">
          Meta scores
        </div>
        <div className="space-y-1.5">
          <BarRow label="Coaching confidence" value={d.meta_scores?.coaching_confidence} testId={`meta-coaching-${idx}`} />
          <BarRow label="Memory confidence" value={d.meta_scores?.memory_confidence} testId={`meta-memory-${idx}`} />
          <BarRow label="Profile completeness" value={d.meta_scores?.profile_completeness} testId={`meta-profile-${idx}`} />
          <BarRow label="Journey depth" value={d.meta_scores?.journey_depth} testId={`meta-journey-${idx}`} />
          <BarRow label="Momentum" value={d.meta_scores?.momentum} testId={`meta-momentum-${idx}`} />
        </div>
      </div>

      {/* COMMERCIAL */}
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">
          Commercial composites
        </div>
        <BarRow
          label="Continued coaching benefit"
          value={d.commercial?.continued_coaching_benefit}
          testId={`commercial-benefit-${idx}`}
        />
        <BarRow
          label="Continued benefit (Plus fit)"
          value={d.commercial?.continued_benefit}
          testId={`commercial-continued-${idx}`}
        />
      </div>

      {/* WHY-PLUS -- the founder's core question */}
      <div className="mb-4 border-l-2 border-emerald-800 pl-3">
        <div className="text-[11px] uppercase tracking-wider text-emerald-400 mb-1">
          Why Plus was {isPlus ? "" : "NOT "}introduced
        </div>
        <div
          data-testid={`why-plus-${idx}`}
          className="text-xs text-neutral-200"
        >
          {isPlus
            ? (d.reason || "engine selected introduce_plus")
            : (d.why_plus_not_introduced ||
                "No gate explanation persisted — see gate trace below.")}
        </div>
      </div>

      {/* PLUS EXPLANATION */}
      {d.plus_explanation && d.plus_explanation.paragraph ? (
        <div className="mb-4 border border-emerald-900/50 rounded-md p-3 bg-emerald-950/20">
          <div className="text-[11px] uppercase tracking-wider text-emerald-400 mb-1">
            Personalized Plus paragraph shown to player
          </div>
          <div
            data-testid={`plus-paragraph-${idx}`}
            className="text-sm text-emerald-100 leading-relaxed"
          >
            {d.plus_explanation.paragraph}
          </div>
          {d.why_this_explanation ? (
            <div
              data-testid={`plus-rationale-${idx}`}
              className="mt-2 text-[11px] text-emerald-300/80 font-mono"
            >
              Why this explanation → {d.why_this_explanation}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* GATE TRACE */}
      {Array.isArray(d.gate_trace) && d.gate_trace.length ? (
        <div className="mb-3">
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">
            Gate trace ({d.gate_trace.length} evaluated)
          </div>
          <div
            className="space-y-2"
            data-testid={`gate-trace-${idx}`}
          >
            {d.gate_trace.map((g, i) => (
              <GateRow key={`${d.at}-${i}`} gate={g} />
            ))}
          </div>
        </div>
      ) : null}

      {/* RAW JSON toggle */}
      <div>
        <button
          type="button"
          data-testid={`decision-raw-toggle-${idx}`}
          className="text-[10px] uppercase tracking-wider text-neutral-500 hover:text-neutral-300"
          onClick={() => setRawOpen((v) => !v)}
        >
          {rawOpen ? "Hide raw JSON" : "Show raw JSON"}
        </button>
        {rawOpen ? (
          <pre
            data-testid={`decision-raw-${idx}`}
            className="mt-2 text-[10px] leading-tight text-neutral-400 bg-neutral-900 rounded p-3 overflow-x-auto"
          >
            {JSON.stringify(d, null, 2)}
          </pre>
        ) : null}
      </div>
    </div>
  );
}

export default function AdminDecisionTrace() {
  const [filterKind, setFilterKind] = useState("session_id");
  const [filterValue, setFilterValue] = useState("");
  const [limit, setLimit] = useState(20);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Decision Trace — Founder Debug";
  }, []);

  const fetchTrace = async () => {
    if (!filterValue.trim()) {
      setError("Enter a session_id, user_id, or browser_id");
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const params = new URLSearchParams();
      params.set(filterKind, filterValue.trim());
      params.set("limit", String(limit));
      const res = await api.get(
        `/admin/founder/decision-trace?${params.toString()}`,
      );
      setData(res.data);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || e.message || String(e);
      setError(`${status || "err"}: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-decision-trace-page"
      className="min-h-screen bg-neutral-950 text-neutral-100 p-6"
    >
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1
            data-testid="decision-trace-title"
            className="text-2xl font-semibold"
          >
            Decision Trace
          </h1>
          <p className="text-sm text-neutral-400 mt-1">
            Every commercial decision Gully makes must be explainable. Enter a
            session, user, or browser id to see the raw signals, gate trace,
            and Plus explanation for the last {limit} decisions.
          </p>
        </div>

        <div
          data-testid="decision-trace-filter-row"
          className="flex flex-wrap gap-2 mb-6 items-center"
        >
          <select
            data-testid="decision-trace-filter-kind"
            value={filterKind}
            onChange={(e) => setFilterKind(e.target.value)}
            className="bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm"
          >
            <option value="session_id">session_id</option>
            <option value="user_id">user_id</option>
            <option value="browser_id">browser_id</option>
          </select>
          <input
            data-testid="decision-trace-filter-value"
            type="text"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
            placeholder="paste an id…"
            className="flex-1 min-w-[240px] bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm font-mono"
          />
          <input
            data-testid="decision-trace-limit"
            type="number"
            min={1}
            max={100}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value) || 20)}
            className="w-20 bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm"
          />
          <button
            type="button"
            data-testid="decision-trace-fetch"
            onClick={fetchTrace}
            disabled={loading}
            className="bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 rounded px-4 py-2 text-sm font-medium"
          >
            {loading ? "Loading…" : "Fetch trace"}
          </button>
        </div>

        {error ? (
          <div
            data-testid="decision-trace-error"
            className="mb-4 border border-rose-900 bg-rose-950/30 text-rose-200 rounded p-3 text-sm"
          >
            {error}
          </div>
        ) : null}

        {data ? (
          <div
            data-testid="decision-trace-summary"
            className="mb-4 text-xs text-neutral-500 font-mono"
          >
            Generated {data.generated_at} · {data.n_decisions} decision
            {data.n_decisions === 1 ? "" : "s"}
          </div>
        ) : null}

        {data && data.decisions?.length ? (
          <div className="space-y-4" data-testid="decision-trace-list">
            {data.decisions.map((d, idx) => (
              <DecisionCard key={`${d.at}-${idx}`} d={d} idx={idx} />
            ))}
          </div>
        ) : null}

        {data && !data.decisions?.length ? (
          <div
            data-testid="decision-trace-empty"
            className="text-neutral-500 text-sm border border-neutral-800 rounded p-6 text-center"
          >
            No decisions found for that id.
          </div>
        ) : null}
      </div>
    </div>
  );
}
